import { ReactNode, useMemo } from 'react';

import {
  RbacOrganizationApiContext,
  RbacOrganizationApiProvider,
} from '@gasbi/shared/web/RbacOrganization';

import {
  useGetDepartmentsIInfiniteQuery,
  useGetDepartmentsQuery,
} from '@/features/departments/departmentsApi';
import {
  useGetEmployeePositionsIInfiniteQuery,
  useGetEmployeePositionsQuery,
} from '@/features/employeePositions/employeePositionsApi';
import {
  useActivateEmployeeByIdMutation,
  useCreateEmployeeMutation,
  useDeactivateEmployeeMutation,
  useGetEmployeeByIdQuery,
  useGetEmployeesInfiniteQuery,
  useLazyGetEmployeeByEmailQuery,
  useReactivateEmployeeByIdMutation,
  useResetEmployeePasswordByIdMutation,
  useUpdateEmployeeByIdMutation,
} from '@/features/employees/employeesApi';
import {
  useGetRbacOrganizationByIdQuery,
  useGetRbacOrganizationLogoQuery,
  useUpdateRbacOrganizationMutation,
} from '@/features/organization/organizationApi';
import { validateOrganizationCodeFn } from '@/features/organization/validateOrganizationCodeFn';
import {
  useGetOrganizationRolesInfiniteQuery,
  useGetRolesForFilterQuery,
} from '@/features/organizations/organizationsApi';

// BMS's organization page has no bank details tab, so it provides no bank accounts.
type BmsRbacOrganizationApi = Omit<
  RbacOrganizationApiContext,
  'bankAccountsApi' | 'bankAccountsMutationsApi' | 'addressesApi'
>;

interface Props {
  children: ReactNode;
}

/**
 * The shared organization page (@gasbi/shared/web/RbacOrganization) over the shared RBAC endpoints BMS mounts, like
 * Supply's (GSB-8317). Departments and positions are BMS's reference books.
 */
const RbacOrganizationApiWrapper = ({ children }: Props) => {
  const value = useMemo<BmsRbacOrganizationApi>(
    () => ({
      organizationApi: {
        useGetOrganizationByIdQuery: useGetRbacOrganizationByIdQuery,
        useGetOrganizationLogoQuery: useGetRbacOrganizationLogoQuery,
        useUpdateOrganizationMutation: useUpdateRbacOrganizationMutation,
      },
      // The shared employees slice names the same endpoints differently: activate = send-activation-request,
      // reactivate = activate.
      employeesApi: {
        useGetEmployeesInfiniteQuery,
        useGetEmployeeByIdQuery,
        useLazyGetEmployeeByEmailQuery,
        useCreateEmployeeMutation,
        useUpdateEmployeeMutation: useUpdateEmployeeByIdMutation,
        useSendActivationRequestMutation: useActivateEmployeeByIdMutation,
        useRemovePasswordMutation: useResetEmployeePasswordByIdMutation,
        useDeactivateEmployeeMutation,
        useActivateEmployeeMutation: useReactivateEmployeeByIdMutation,
      },
      departmentsApi: {
        useGetDepartmentsQuery,
        useGetDepartmentsInfiniteQuery: useGetDepartmentsIInfiniteQuery,
      },
      positionsApi: {
        useGetPositionsQuery: useGetEmployeePositionsQuery,
        useGetPositionsInfiniteQuery: useGetEmployeePositionsIInfiniteQuery,
      },
      rolesApi: {
        useGetRolesForFilterQuery,
        useGetOrganizationRolesInfiniteQuery,
      },
      validateCodeFn: validateOrganizationCodeFn,
    }),
    [],
  );

  return (
    <RbacOrganizationApiProvider value={value as RbacOrganizationApiContext}>
      {children}
    </RbacOrganizationApiProvider>
  );
};

export default RbacOrganizationApiWrapper;
